// LearnCore Frontend - 家长管理页面

import React, { useState, useEffect } from 'react';
// Layout 已由顶层 App 包裹，此处不再引入
import DKPBalanceComponent from '../components/DKPBalance';
import TaskList from '../components/TaskList';
import CreateRewardForm from '../components/CreateRewardForm';
import EditRewardForm from '../components/EditRewardForm';
import RewardManagementList from '../components/RewardManagementList';
import { dkpApi } from '../api/tasks';
import { parentApi } from '../api/parent';
import type {
  DKPTransaction,
  RewardTemplate
} from '../types/tasks-rewards';
import type {
  LinkedChild
} from '../types';

const ParentDashboardPage: React.FC = () => {
  const [children, setChildren] = useState<LinkedChild[]>([]);
  const [selectedChild, setSelectedChild] = useState<LinkedChild | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<DKPTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'transactions' | 'rewards'>('overview');
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [addChildForm, setAddChildForm] = useState({
    username: '',
    password: '',
    display_name: '',
    relationship: 'parent' as 'parent' | 'guardian' | 'tutor',
    permissions: 'full' as 'full' | 'limited' | 'view_only'
  });

  // 奖励管理相关状态
  const [rewardViewMode, setRewardViewMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingReward, setEditingReward] = useState<RewardTemplate | null>(null);
  const [rewardListKey, setRewardListKey] = useState(0); // 用于刷新奖励列表

  useEffect(() => {
    console.log('[ParentDashboard] 组件挂载，开始加载...');
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildData();
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      console.log('[ParentDashboard] 开始加载孩子列表...');
      setLoading(true);
      const response = await parentApi.getChildren();
      
      console.log('[ParentDashboard] API响应:', response);
      
      // 后端直接返回数据格式：{children: [], total: 0}
      // 而不是包装格式：{success: true, data: {children: [], total: 0}}
      if (response.children !== undefined) {
        console.log('[ParentDashboard] 成功加载孩子列表:', response.children);
        setChildren(response.children || []);
        if (response.children && response.children.length > 0) {
          setSelectedChild(response.children[0]);
        }
      } else {
        console.error('[ParentDashboard] API返回错误:', response.error);
        setError(response.error || '加载孩子列表失败');
      }
    } catch (err) {
      console.error('[ParentDashboard] 加载孩子列表异常:', err);
      setError('加载孩子列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadChildData = async () => {
    if (!selectedChild) return;

    try {
      // 加载最近的交易记录
      const transactionsResponse = await dkpApi.getDKPTransactions({
        user_id: selectedChild.id,
        limit: 10
      });
      setRecentTransactions(transactionsResponse.transactions || []);
    } catch (err) {
      console.error('Failed to load child data:', err);
    }
  };

  const handleManualAdjustDKP = async (amount: number, reason: string) => {
    if (!selectedChild) return;

    try {
      await dkpApi.adjustDKP({
        child_id: selectedChild.id,
        amount,
        reason
      });
      
      // 重新加载数据
      loadChildren();
      loadChildData();
    } catch (err) {
      console.error('Failed to adjust DKP:', err);
      setError('调整DKP失败');
    }
  };

  const handleAddChild = async () => {
    try {
      console.log('[ParentDashboard] 开始添加孩子:', addChildForm);
      const response = await parentApi.createChild(addChildForm);
      console.log('[ParentDashboard] 添加孩子响应:', response);

      // 后端创建孩子成功时返回的数据格式检查
      if (response.child || response.message) {
        // 重新加载孩子列表
        await loadChildren();
        // 关闭模态框并重置表单
        setShowAddChildModal(false);
        setAddChildForm({
          username: '',
          password: '',
          display_name: '',
          relationship: 'parent',
          permissions: 'full'
        });
      } else {
        setError(response.error || '添加孩子失败');
      }
    } catch (err) {
      setError('添加孩子失败');
      console.error('[ParentDashboard] 添加孩子异常:', err);
    }
  };

  // 奖励管理相关处理函数
  const handleCreateReward = (reward: RewardTemplate) => {
    setRewardViewMode('list');
    setRewardListKey(prev => prev + 1); // 刷新奖励列表
    // 显示成功提示
    alert(`奖励"${reward.title}"创建成功！`);
    console.log('奖励创建成功:', reward);
  };

  const handleEditReward = (reward: RewardTemplate) => {
    setEditingReward(reward);
    setRewardViewMode('edit');
  };

  const handleUpdateReward = (reward: RewardTemplate) => {
    setRewardViewMode('list');
    setEditingReward(null);
    setRewardListKey(prev => prev + 1); // 刷新奖励列表
    // 显示成功提示
    alert(`奖励"${reward.title}"更新成功！`);
    console.log('奖励更新成功:', reward);
  };

  const handleDeleteReward = (reward: RewardTemplate) => {
    setRewardListKey(prev => prev + 1); // 刷新奖励列表
    console.log('奖励删除成功:', reward);
  };

  const handleRewardViewModeChange = (mode: 'list' | 'create') => {
    setRewardViewMode(mode);
    if (mode === 'list') {
      setEditingReward(null);
    }
  };

  const getTransactionTypeText = (type: string): string => {
    switch (type) {
      case 'task_completion': return '任务完成';
      case 'reward_redemption': return '奖励兑换';
      case 'manual_adjustment': return '手动调整';
      case 'bonus': return '奖励';
      case 'penalty': return '惩罚';
      default: return type;
    }
  };

  const getTransactionColor = (amount: number): string => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    console.log('[ParentDashboard] 显示加载状态');
    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
    );
  }

  if (error) {
    console.log('[ParentDashboard] 显示错误状态:', error);
    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800">{error}</div>
          </div>
        </div>
    );
  }

  console.log('[ParentDashboard] 渲染主界面，孩子数量:', children.length);

  return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* 页面标题 */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">家长管理</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">管理孩子的学习任务和奖励</p>
          </div>
          <button
            onClick={() => {
              console.log('[ParentDashboard] 点击添加孩子按钮');
              setShowAddChildModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            添加孩子
          </button>
        </div>

        {/* 没有孩子时的引导界面 */}
        {children.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">还没有添加孩子</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              添加您的第一个孩子账户，开始管理他们的学习任务和奖励系统。孩子可以使用用户名和密码登录自己的学习账户。
            </p>
            <button
              onClick={() => setShowAddChildModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              添加第一个孩子
            </button>
          </div>
        )}

        {/* 孩子选择器 */}
        {children.length > 1 && (
          <div className="mb-6">
            <div className="flex gap-4 overflow-x-auto pb-2">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => setSelectedChild(child)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors whitespace-nowrap ${
                    selectedChild?.id === child.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                      {child.display_name?.[0] || child.username?.[0] || '?'}
                    </div>
                    <div className="text-left">
                      <div className="font-medium">{child.display_name || child.username}</div>
                      <div className="text-sm opacity-75">{child.current_dkp} DKP</div>
                    </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedChild && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* 左侧边栏 */}
            <div className="lg:col-span-1 space-y-6">
              {/* 孩子信息卡片 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                    {selectedChild.display_name?.[0] || selectedChild.username?.[0] || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{selectedChild.display_name || selectedChild.username}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedChild.email || selectedChild.username}</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">当前DKP</span>
                    <span className="font-medium text-orange-600 dark:text-orange-400">{selectedChild.current_dkp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">完成任务</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{selectedChild.completed_tasks}/{selectedChild.total_tasks}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">连续天数</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{selectedChild.consecutive_days}天</span>
                  </div>
                </div>
              </div>

              {/* 快速操作 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">快速操作</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleManualAdjustDKP(50, '表现优秀奖励')}
                    className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    +50 DKP 奖励
                  </button>
                  <button
                    onClick={() => handleManualAdjustDKP(-20, '违反规定扣分')}
                    className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    -20 DKP 扣分
                  </button>
                  <button className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                    分配新任务
                  </button>
                </div>
              </div>

              {/* 最近活动 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">最近活动</h3>
                <div className="space-y-2">
                  {(recentTransactions || []).slice(0, 5).map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400 truncate">{getTransactionTypeText(transaction.transaction_type)}</span>
                      <span className={`font-medium ${getTransactionColor(transaction.amount)}`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 主内容区域 */}
            <div className="lg:col-span-3">
              {/* 标签页 */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex -mb-px">
                    {[
                      { key: 'overview', label: '概览' },
                      { key: 'tasks', label: '任务管理' },
                      { key: 'transactions', label: '交易记录' },
                      { key: 'rewards', label: '奖励管理' }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key as any)}
                        className={`py-3 px-6 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === tab.key
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* 标签页内容 */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DKPBalanceComponent userId={selectedChild.id} showTransactions={true} />
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">学习统计</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-400">任务完成率</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {Math.round((selectedChild.completed_tasks / selectedChild.total_tasks) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{
                              width: `${(selectedChild.completed_tasks / selectedChild.total_tasks) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{selectedChild.consecutive_days}</div>
                          <div className="text-sm text-blue-700 dark:text-blue-300">连续天数</div>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3">
                          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{selectedChild.current_dkp}</div>
                          <div className="text-sm text-orange-700 dark:text-orange-300">当前DKP</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'tasks' && (
                <TaskList
                  userId={selectedChild.id}
                  showActions={true}
                  onTaskUpdate={() => loadChildData()}
                  onTaskDelete={() => loadChildData()}
                />
              )}

              {activeTab === 'transactions' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">交易记录</h3>
                  <div className="space-y-2">
                    {(recentTransactions || []).map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {getTransactionTypeText(transaction.transaction_type)}
                          </div>
                          {transaction.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{transaction.description}</div>
                          )}
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(transaction.created_at).toLocaleString('zh-CN')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-medium ${getTransactionColor(transaction.amount)}`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            余额: {transaction.balance_after}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'rewards' && (
                <div className="space-y-6">
                  {/* 奖励管理头部 */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">奖励管理</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          创建和管理自定义奖励，激励孩子完成任务
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {rewardViewMode !== 'list' && (
                          <button
                            onClick={() => handleRewardViewModeChange('list')}
                            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                        )}
                        {rewardViewMode === 'list' && (
                          <button
                            onClick={() => handleRewardViewModeChange('create')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            创建奖励
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 奖励管理内容 */}
                  {rewardViewMode === 'list' && (
                    <RewardManagementList
                      key={rewardListKey}
                      onEdit={handleEditReward}
                      onDelete={handleDeleteReward}
                    />
                  )}

                  {rewardViewMode === 'create' && (
                    <CreateRewardForm
                      onSuccess={handleCreateReward}
                      onCancel={() => handleRewardViewModeChange('list')}
                    />
                  )}

                  {rewardViewMode === 'edit' && editingReward && (
                    <EditRewardForm
                      rewardId={editingReward.id}
                      onSuccess={handleUpdateReward}
                      onCancel={() => handleRewardViewModeChange('list')}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 添加孩子模态框 */}
        {showAddChildModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">添加孩子账户</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称</label>
                  <input
                    type="text"
                    value={addChildForm.display_name}
                    onChange={(e) => setAddChildForm({...addChildForm, display_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="孩子的姓名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">用户名</label>
                  <input
                    type="text"
                    value={addChildForm.username}
                    onChange={(e) => setAddChildForm({...addChildForm, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="登录用户名（3-20个字符）"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">密码</label>
                  <input
                    type="password"
                    value={addChildForm.password}
                    onChange={(e) => setAddChildForm({...addChildForm, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="至少6位密码"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">关系</label>
                  <select
                    value={addChildForm.relationship}
                    onChange={(e) => setAddChildForm({...addChildForm, relationship: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="parent">父母</option>
                    <option value="guardian">监护人</option>
                    <option value="tutor">家教</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">权限</label>
                  <select
                    value={addChildForm.permissions}
                    onChange={(e) => setAddChildForm({...addChildForm, permissions: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="full">完全权限</option>
                    <option value="limited">有限权限</option>
                    <option value="view_only">只读权限</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddChildModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleAddChild}
                  disabled={!addChildForm.username || !addChildForm.password || !addChildForm.display_name}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  添加
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default ParentDashboardPage;